import {FC, useMemo, useState} from 'react';

import {Box, Typography, useMediaQuery} from '@mui/material';
import {t} from 'i18next';
import {Trans} from 'react-i18next';

import Button from 'components/button/Button';
import {config} from 'config/env';
import theme from 'config/theme';
import {useAppSelector, useAppDispatch} from 'hooks';
import {useAppMode} from 'hooks/useAppMode';
import {useBasketCalculations} from 'hooks/useBasketCalculations';
import {useSelectedItemsLocalStorage} from 'hooks/useSelectedItemsLocalStorage';
import ReactNative from 'services/react-native';
import SendBrowserEvent from 'services/send-browser-event';
import {checkedUnselectItemIdsAction, selectItemIdsAction} from 'store/actions/basket-action';
import {
  ShippingInfo,
  MarketplaceShopType,
  ProductSectionTypes,
  BasketItemType,
  BasketLineItem,
  ExpressTimeFrame
} from 'types';
import {
  checkIfAnyItemFromSectionIsSelected,
  getAlcoholVolumeInLiters,
  getIdsFromBasketItems,
  isAllItemsFromThisSectionAreSelected,
  isTimeEligibleForExpress
} from 'utils';
import {isUnifiedBasketEnabled, safeToFixed} from 'utils/helper';
import {BasketFreeDeliveryBanner} from 'views/basket-banners';
import {BasketShippingFeeDiscountBanner} from 'views/basket-banners/components/BasketShippingFeeDiscountBanner';
import {BasketShippingFeePromotionBanner} from 'views/basket-banners/components/BasketShippingFeePromotionBanner';
import {BasketDeliveryDetails1PModal, BasketDeliveryDetails3PModal} from 'views/basket-modals';
import {ExpressDeliveryDetailsModal} from 'views/basket-modals/ExpressDeliveryDetailsModal';
import {ExpressOutOfWorkingHoursModal} from 'views/basket-modals/ExpressOutOfWorkingHoursModal';

import {
  BasketExpressHeader,
  BasketSellerHeader1P,
  BasketSellerHeader3P,
  BasketSellerHeaderAlco,
  BasketSellerHeaderBulky,
  BasketSellerHeaderMakroFleet,
  BasketSellerHeaderPreOrder
} from './basketSellerHeader.component';
import {
  StyledAvailableProducts,
  StyledSectionHeader,
  StyledUnavailableAlcoProductsLessThenAllowed,
  StyledUnavailableProducts
} from './styles';
import {ALCOHOL_VOLUME_THRESHOLD} from '../../constants/index';
import {BasketDeliveryDetailsBulkyModalV2} from '../basket-modals/BasketDeliveryDetailsBulkyModalV2';
import {BasketDeliveryDetailsMakroFleetModal} from '../basket-modals/BasketDeliveryDetailsMakroFleetModal';
import {DeliveryDetailsPreOrderModal} from '../basket-modals/DeliveryDetailsPreOrderModal';

export type SectionHeaderProps = {
  title: string;
  description: string;
  index: number;
  header?: string;
  marketplaceType?: string;
  type?: ProductSectionTypes;
  onlyBulkyItems?: boolean;
  productId?: string;
  onlyPreOrderItems?: boolean;
  only1PxBulkyItems?: boolean;
  onlyBulkyAnd1PxBulkyItems?: boolean;
  shippingInfo?: ShippingInfo;
  data: BasketLineItem[];
  deleteUnavailableItems: () => void;
  pure1PxProducts?: boolean;
};

export type SectionHeaderV2Props = SectionHeaderProps & {
  shopId: number;
  shippingFeePromotionText?: string;
};
export const SectionHeaderV2: FC<SectionHeaderV2Props> = ({
  title,
  description,
  type,
  index,
  header,
  marketplaceType,
  shopId,
  onlyBulkyItems,
  onlyPreOrderItems,
  only1PxBulkyItems,
  onlyBulkyAnd1PxBulkyItems,
  shippingInfo,
  data,
  deleteUnavailableItems,
  shippingFeePromotionText,
  pure1PxProducts
}) => {
  const dispatch = useAppDispatch();
  const {isExpress} = useAppMode();
  const selectedItemsLocalStorage = useSelectedItemsLocalStorage();
  const {basket} = useAppSelector((state) => state?.basket);
  const isMobileView = useMediaQuery(theme.breakpoints.only('mobile'));
  const {defaultShippingFee, priceUnit} = useBasketCalculations();
  const [open1PModal, setOpen1PModal] = useState(false);
  const [open3PModal, setOpen3PModal] = useState(false);
  const [openBulkyModal, setOpenBulkyModal] = useState(false);
  const [openPreOrderModal, setOpenPreOrderModal] = useState(false);
  const [openMakroFleetModal, setOpenMakroFleetModal] = useState(false);
  const [openExpressModal, setOpenExpressModal] = useState(false);
  const [openExpressOutOfTimeModal, setOpenExpressOutOfTimeModal] = useState(false);

  const isUnifiedBasketEnabledFlag = isUnifiedBasketEnabled();

  const isUnAvailableSection = type === ProductSectionTypes.OUT_OF_STOCK_PRODUCTS;
  const isAvailableSection = type === ProductSectionTypes.AVAILABLE_PRODUCTS;
  const isExpressSection = type === ProductSectionTypes.EXPRESS_PRODUCTS;
  const isAlcoholProhibitedPeriod = type === ProductSectionTypes.ALCOHOL_PROHIBITED_PERIOD;
  const isAlcoholLessThanRequired = type === ProductSectionTypes.ALCOHOL_LESS_THAN_REQUIRED;
  const isAlcoholLicenseExpired = type === ProductSectionTypes.ALCOHOL_EXPIRED_LICENSE;
  const isBulky = marketplaceType === ProductSectionTypes.BULKY_PRODUCTS;
  const isPreOrder = marketplaceType === ProductSectionTypes.PRE_ORDER_PRODUCTS;
  const is1PSeller = marketplaceType === MarketplaceShopType.CONSIGNMENT;
  const isAlco = marketplaceType === ProductSectionTypes.ALCOHOL_PRODUCTS;
  const isMakroFleet = marketplaceType === BasketItemType.MAKRO_FLEET;
  const is1Px = marketplaceType === BasketItemType.ONEPX;
  const isReactNativeWebView = ReactNative.isReactNativeWebView();
  const isMobileScreen = isReactNativeWebView || isMobileView;

  const selectedItems = selectedItemsLocalStorage.get();

  const isAnyItemFromTheSectionSelected = useMemo(
    () => checkIfAnyItemFromSectionIsSelected(data, selectedItems),
    [data, selectedItems]
  );

  const is3PSeller = !is1PSeller;

  if (!basket?.prices) {
    return null;
  }

  const {
    subtotal = 0,
    shippingFee = 0,
    originalShippingFee,
    is3PSeller: is3PSellerShop
  } = basket?.prices.priceBreakdown.find((item) => item.shopId === shopId) || {};

  const hasShippingFeePromotionApplied = !!(
    isAnyItemFromTheSectionSelected &&
    originalShippingFee &&
    originalShippingFee > shippingFee
  ); // forcing this value to be boolean instead of the react-ambigues value of 0

  const shippingFeePromotion = (originalShippingFee || 0) - (shippingFee || 0);

  const getRemainingAmountOfAlcohol = () => {
    const remaining =
      (basket.alcoholLitersThreshold || ALCOHOL_VOLUME_THRESHOLD) - getAlcoholVolumeInLiters(basket.unavailableItems) ||
      0;
    return remaining >= 0.01 ? remaining : 0.01;
  };

  const toggle1PDeliveryModal = () => setOpen1PModal(!open1PModal);

  const toggle3PDeliveryModal = () => setOpen3PModal(!open3PModal);

  const toggleMakroFleetDeliveryModal = () => setOpenMakroFleetModal((flag) => !flag);

  const toggleExpressModal = () => {
    const isDuringExpressHours = isTimeEligibleForExpress(
      basket.storeInfo?.[0]?.expressDeliveryTimeFrame as ExpressTimeFrame
    );
    isDuringExpressHours ? setOpenExpressModal((prev) => !prev) : setOpenExpressOutOfTimeModal((prev) => !prev);
  };

  const openBulkyDeliveryModal = () => setOpenBulkyModal(true);
  const closeBulkyDeliveryModal = () => setOpenBulkyModal(false);

  const openPreOrderDeliveryModal = () => setOpenPreOrderModal(true);
  const closePreOrderDeliveryModal = () => {
    setOpenPreOrderModal(false);
  };

  const toggleSellerDeliveryModal = () => {
    if (onlyPreOrderItems) {
      openPreOrderDeliveryModal();
    } else if (onlyBulkyItems || onlyBulkyAnd1PxBulkyItems || only1PxBulkyItems) {
      openBulkyDeliveryModal();
    } else {
      toggle1PDeliveryModal();
    }
  };

  const onSelectSection = (e: any) => {
    const checked = e.target.checked;
    const sectionItemsIds = getIdsFromBasketItems(data);

    if (checked) {
      dispatch(selectItemIdsAction(sectionItemsIds));
    } else {
      dispatch(checkedUnselectItemIdsAction(sectionItemsIds));
    }
  };

  const isSelectedAllBasketItems = basket.basketItems?.length === selectedItems?.length;
  const isAllItemsFromThisSectionSelected = isAllItemsFromThisSectionAreSelected(data, selectedItems);

  return (
    <>
      <StyledSectionHeader
        className={`v2 ${
          isUnAvailableSection || isAlcoholProhibitedPeriod || isAlcoholLessThanRequired || isAlcoholLicenseExpired
            ? 'unavailable'
            : ''
        }`}
        key={index}
      >
        {!!header?.length && (
          <>
            {(isUnAvailableSection || isAlcoholProhibitedPeriod || isAlcoholLicenseExpired) && (
              <StyledUnavailableProducts>
                <Box pl="12px">
                  <Typography component="p" color="primary.dark" fontWeight={700}>
                    {header}
                  </Typography>
                </Box>
                <Box>
                  <Button onClick={deleteUnavailableItems}>{t('flexiBasket.deleteAll')}</Button>
                </Box>
              </StyledUnavailableProducts>
            )}
            {isAvailableSection && (
              <StyledAvailableProducts>
                <Typography component="p" color="success.dark" fontWeight={700}>
                  {header}
                </Typography>
              </StyledAvailableProducts>
            )}
          </>
        )}

        {isAlcoholLessThanRequired && (
          <>
            <BasketSellerHeaderAlco
              isUnavailable
              title={t('alcohol.title.alcoProducts')}
              message={t('alcohol.label.readT&C')}
              onClick={() => SendBrowserEvent.onAlcoTermsAndConditionsPress()}
              minimumQuantityError={t('alcohol.message.minimumPurchase', {
                amount: basket.alcoholLitersThreshold || ALCOHOL_VOLUME_THRESHOLD
              })}
              onSelectSection={onSelectSection}
              isSelectedAllBasketItems={isSelectedAllBasketItems}
              isAllItemsFromThisSectionSelected={isAllItemsFromThisSectionSelected}
              onClickDeliveryInfo={toggleSellerDeliveryModal}
              isReactNativeView={isMobileScreen}
            />
            <StyledUnavailableAlcoProductsLessThenAllowed>
              <Typography component="p" color="primary.dark" fontWeight={400}>
                <Trans
                  i18nKey="alcohol.message.addMoreAlcohol2"
                  values={{volume: +safeToFixed(getRemainingAmountOfAlcohol())}}
                />
              </Typography>
            </StyledUnavailableAlcoProductsLessThenAllowed>
          </>
        )}

        {!!title && (
          <>
            {is1PSeller &&
              !isBulky &&
              !isPreOrder &&
              !isAlco &&
              !isMakroFleet &&
              !is3PSeller &&
              !onlyPreOrderItems &&
              !onlyBulkyItems &&
              !onlyBulkyAnd1PxBulkyItems &&
              !only1PxBulkyItems &&
              !(isUnifiedBasketEnabledFlag && isExpressSection) && (
                <BasketSellerHeader1P
                  priceUnit={priceUnit}
                  onClick={isExpress ? toggleExpressModal : toggleSellerDeliveryModal}
                  subtotal={subtotal}
                  currentShippingFee={shippingFee as number}
                  defaultShippingFee={defaultShippingFee}
                  deliveryBy={description}
                  onlyBulkyItems={!!onlyBulkyItems}
                  onlyPreOrderItems={!!onlyPreOrderItems}
                  onSelectSection={onSelectSection}
                  description={description}
                  isSelectedAllBasketItems={isSelectedAllBasketItems}
                  isAllItemsFromThisSectionSelected={isAllItemsFromThisSectionSelected}
                  isReactNativeView={isMobileScreen}
                />
              )}

            {is3PSeller && !is1Px && !isBulky && !isPreOrder && !isAlco && !isMakroFleet && (
              <BasketSellerHeader3P
                priceUnit={priceUnit}
                sellerName={title}
                deliveryBy={description}
                onClick={toggle3PDeliveryModal}
                subtotal={subtotal}
                shippingFee={shippingFee as number}
                originalShippingFee={originalShippingFee}
                onSelectSection={onSelectSection}
                isSelectedAllBasketItems={isSelectedAllBasketItems}
                isAllItemsFromThisSectionSelected={isAllItemsFromThisSectionSelected}
                isReactNativeView={isMobileScreen}
                showDeliveryFee={isAnyItemFromTheSectionSelected}
                shopLogo={data[0].productDetails.sellerDetails.shopLogo}
              />
            )}

            {isMakroFleet && (
              <BasketSellerHeaderMakroFleet
                priceUnit={priceUnit}
                sellerName={title}
                description={t('label.chooseDeliverySlot')}
                onClick={toggleMakroFleetDeliveryModal}
                subtotal={subtotal}
                shippingFee={shippingFee as number}
                onSelectSection={onSelectSection}
                isSelectedAllBasketItems={isSelectedAllBasketItems}
                isAllItemsFromThisSectionSelected={isAllItemsFromThisSectionSelected}
                isReactNativeView={isMobileScreen}
              />
            )}

            {(isBulky || onlyBulkyItems || is1Px || only1PxBulkyItems) && !isPreOrder && isAvailableSection && (
              <BasketSellerHeaderBulky
                onClick={openBulkyDeliveryModal}
                deliveryBy={description}
                title={title}
                onlyBulkyItems={!!onlyBulkyItems}
                only1PxBulkyItems={!!only1PxBulkyItems}
                is1PSeller={is1PSeller || (onlyBulkyAnd1PxBulkyItems && isBulky)}
                is1PX={is1Px}
                onSelectSection={onSelectSection}
                isSelectedAllBasketItems={isSelectedAllBasketItems}
                isAllItemsFromThisSectionSelected={isAllItemsFromThisSectionSelected}
                isReactNativeView={isMobileScreen}
              />
            )}

            {(isPreOrder || onlyPreOrderItems) && isAvailableSection && (
              <BasketSellerHeaderPreOrder
                onClick={openPreOrderDeliveryModal}
                title={title}
                deliveryBy={description}
                onlyPreOrderItems={!!onlyPreOrderItems}
                onSelectSection={onSelectSection}
                isSelectedAllBasketItems={isSelectedAllBasketItems}
                isAllItemsFromThisSectionSelected={isAllItemsFromThisSectionSelected}
                isReactNativeView={isMobileScreen}
                hidePreOrderDates={config.HIDE_PRE_ORDER_DATES}
              />
            )}

            {isAlco && (
              <BasketSellerHeaderAlco
                title={title}
                message={description}
                onSelectSection={onSelectSection}
                onClick={() => SendBrowserEvent.onAlcoTermsAndConditionsPress()}
                isSelectedAllBasketItems={isSelectedAllBasketItems}
                isAllItemsFromThisSectionSelected={isAllItemsFromThisSectionSelected}
                onClickDeliveryInfo={toggleSellerDeliveryModal}
                isReactNativeView={isMobileScreen}
              />
            )}
          </>
        )}
        {isExpressSection && isUnifiedBasketEnabledFlag && (
          <BasketExpressHeader
            priceUnit={priceUnit}
            onClick={toggle3PDeliveryModal}
            subtotal={subtotal}
            shippingFee={shippingFee as number}
            originalShippingFee={originalShippingFee}
            onSelectSection={onSelectSection}
            isSelectedAllBasketItems={isSelectedAllBasketItems}
            isAllItemsFromThisSectionSelected={isAllItemsFromThisSectionSelected}
            isReactNativeView={isReactNativeWebView}
          />
        )}
      </StyledSectionHeader>

      {hasShippingFeePromotionApplied && <BasketShippingFeeDiscountBanner discountAmount={shippingFeePromotion} />}

      {(is1PSeller || onlyBulkyAnd1PxBulkyItems) && (isAvailableSection || isExpressSection) && (
        <BasketFreeDeliveryBanner
          shippingFee={shippingFee}
          noItemSelected={!isAnyItemFromTheSectionSelected}
          is3P={is3PSellerShop}
        />
      )}

      {!!shippingFeePromotionText && <BasketShippingFeePromotionBanner message={shippingFeePromotionText as string} />}

      <BasketDeliveryDetails1PModal
        open={open1PModal}
        onClose={toggle1PDeliveryModal}
        isReactNativeWebView={isMobileScreen}
      />
      <BasketDeliveryDetails3PModal
        open={open3PModal}
        onClose={toggle3PDeliveryModal}
        sellerName={title}
        deliveryBy={description}
        isReactNativeWebView={isMobileScreen}
      />
      <BasketDeliveryDetailsBulkyModalV2
        open={openBulkyModal}
        onClose={closeBulkyDeliveryModal}
        is1Px={is1Px || only1PxBulkyItems}
        shippingInfo={shippingInfo}
        isReactNativeWebView={isMobileScreen}
        pure1PxProducts={pure1PxProducts}
      />
      <BasketDeliveryDetailsMakroFleetModal
        open={openMakroFleetModal}
        deliveryBy={description}
        sellerName={title}
        onClose={toggleMakroFleetDeliveryModal}
        isReactNativeWebView={isMobileScreen}
      />
      <DeliveryDetailsPreOrderModal
        open={openPreOrderModal}
        deliveryBy={description}
        onClose={closePreOrderDeliveryModal}
        isReactNativeWebView={isMobileScreen}
      ></DeliveryDetailsPreOrderModal>
      {isExpress && !isUnifiedBasketEnabledFlag && (
        <>
          <ExpressDeliveryDetailsModal
            open={openExpressModal}
            onClose={toggleExpressModal}
            isReactNativeWebView={isMobileScreen}
          />
          <ExpressOutOfWorkingHoursModal open={openExpressOutOfTimeModal} onClose={toggleExpressModal} />
        </>
      )}
    </>
  );
};

SectionHeaderV2.displayName = 'BasketDrawerSectionHeaderV2';
